// src/features/tuition/components/online/TutorialManagement.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Snackbar,
  Alert
} from '@mui/material';
import { THEME_COLORS } from '../../../../styles/theme';

import TutorialList from './TutorialList';
import TutorialForm from './TutorialForm';
import TutorialView from './TutorialView';

// Import mock data fetching services
import { 
  fetchTutorials, 
  fetchTutorialById, 
  createTutorial, 
  updateTutorial, 
  deleteTutorial,
  publishTutorial,
  unpublishTutorial,
  completeActivity
} from '../../services/tutorialService';

const TutorialManagement = ({ userId }) => {
  const [view, setView] = useState('list'); // 'list', 'form', 'view'
  const [tutorials, setTutorials] = useState([]);
  const [currentTutorial, setCurrentTutorial] = useState(null);
  const [selectedTutorialId, setSelectedTutorialId] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  useEffect(() => {
    loadData();
  }, [userId]);
  
  const loadData = async () => {
    setLoading(true);
    try {
      // In a real app, these would be API calls
      const tutorialsData = await fetchTutorials(userId);
      setTutorials(tutorialsData);
      
      // Load subjects for the dropdown
      const mockSubjects = [
        { id: 's1', name: 'Mathematics', grade: '10' },
        { id: 's2', name: 'Physical Sciences', grade: '11' },
        { id: 's3', name: 'English Language', grade: '9' },
        { id: 's4', name: 'Biology', grade: '12' }
      ];
      setSubjects(mockSubjects);
    } catch (error) {
      console.error("Error loading tutorials:", error);
      showNotification("Failed to load tutorials", "error");
    } finally {
      setLoading(false);
    }
  };
  
  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };
  
  const handleCloseNotification = () => {
    setNotification(prev => ({
      ...prev,
      open: false
    }));
  };
  
  // Tutorial CRUD operations
  const handleAddTutorial = () => {
    setCurrentTutorial(null);
    setView('form');
  };
  
  const handleEditTutorial = async (tutorialId) => {
    try {
      setLoading(true);
      const tutorial = await fetchTutorialById(tutorialId);
      setCurrentTutorial(tutorial);
      setView('form');
    } catch (error) {
      console.error("Error loading tutorial for edit:", error);
      showNotification("Failed to load tutorial details", "error");
    } finally {
      setLoading(false);
    }
  };
  
  const handleViewTutorial = async (tutorialId) => {
    try {
      setLoading(true);
      const tutorial = await fetchTutorialById(tutorialId);
      setCurrentTutorial(tutorial);
      setSelectedTutorialId(tutorialId);
      setView('view');
    } catch (error) {
      console.error("Error loading tutorial for view:", error);
      showNotification("Failed to load tutorial details", "error");
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteTutorial = async (tutorialId) => {
    if (!window.confirm("Are you sure you want to delete this tutorial? This action cannot be undone.")) {
      return;
    }
    
    try {
      await deleteTutorial(tutorialId);
      setTutorials(tutorials.filter(tutorial => tutorial.id !== tutorialId));
      showNotification("Tutorial deleted successfully");
    } catch (error) {
      console.error("Error deleting tutorial:", error);
      showNotification("Failed to delete tutorial", "error");
    }
  };
  
  const handlePublishTutorial = async (tutorialId) => {
    try {
      await publishTutorial(tutorialId);
      
      // Update local state
      setTutorials(tutorials.map(tutorial => 
        tutorial.id === tutorialId 
          ? { ...tutorial, status: 'published', publishedAt: new Date().toISOString() } 
          : tutorial
      ));
      
      showNotification("Tutorial published successfully");
    } catch (error) {
      console.error("Error publishing tutorial:", error);
      showNotification("Failed to publish tutorial", "error");
    }
  };
  
  const handleUnpublishTutorial = async (tutorialId) => {
    try {
      await unpublishTutorial(tutorialId);
      
      // Update local state
      setTutorials(tutorials.map(tutorial => 
        tutorial.id === tutorialId 
          ? { ...tutorial, status: 'unpublished' } 
          : tutorial
      ));
      
      showNotification("Tutorial unpublished successfully");
    } catch (error) {
      console.error("Error unpublishing tutorial:", error);
      showNotification("Failed to unpublish tutorial", "error");
    }
  };
  
  const handleFormClose = () => {
    setView('list');
    setCurrentTutorial(null);
  };
  
  const handleFormSubmit = async (tutorialData) => {
    try {
      if (currentTutorial) {
        // Update existing tutorial
        const updatedTutorial = await updateTutorial(currentTutorial.id, tutorialData);
        
        // Update tutorials list
        setTutorials(tutorials.map(tutorial => 
          tutorial.id === currentTutorial.id ? updatedTutorial : tutorial
        ));
        
        showNotification("Tutorial updated successfully");
      } else {
        // Create new tutorial
        const newTutorial = await createTutorial({ ...tutorialData, userId });
        
        // Add to tutorials list
        setTutorials([newTutorial, ...tutorials]);
        
        showNotification("Tutorial created successfully");
      }
      
      // Go back to list view
      setView('list');
    } catch (error) {
      console.error("Error saving tutorial:", error);
      showNotification("Failed to save tutorial", "error");
    }
  };
  
  const handleCompleteActivity = async (tutorialId, completionData) => {
    try {
      // Send completion data to server
      await completeActivity(tutorialId, completionData);
      
      // Update local state
      setCurrentTutorial(prev => {
        if (!prev) return null;
        
        const completedActivities = prev.completedActivities || [];
        return {
          ...prev,
          completedActivities: [...completedActivities, completionData]
        };
      });
      
      // Update tutorials list
      setTutorials(tutorials.map(tutorial => {
        if (tutorial.id !== tutorialId) return tutorial;
        
        const completedActivities = tutorial.completedActivities || [];
        return {
          ...tutorial,
          completedActivities: [...completedActivities, completionData]
        };
      }));
      
      showNotification("Activity completed successfully");
    } catch (error) {
      console.error("Error completing activity:", error);
      showNotification("Failed to complete activity", "error");
    }
  };
  
  const getViewContent = () => {
    switch (view) {
      case 'form':
        return (
          <TutorialForm
            open={true}
            onClose={handleFormClose}
            onSubmit={handleFormSubmit}
            initialData={currentTutorial}
            subjectId={currentTutorial?.subjectId}
            userId={userId}
          />
        );
      case 'view':
        return (
          <TutorialView
            tutorial={currentTutorial}
            onBack={() => setView('list')}
            onEdit={() => handleEditTutorial(selectedTutorialId)}
            onCompleteActivity={handleCompleteActivity}
            loading={loading}
          />
        );
      case 'list':
      default:
        return (
          <TutorialList
            tutorials={tutorials}
            subjects={subjects}
            onAddTutorial={handleAddTutorial}
            onEditTutorial={handleEditTutorial}
            onDeleteTutorial={handleDeleteTutorial}
            onViewTutorial={handleViewTutorial}
            onPublishTutorial={handlePublishTutorial}
            onUnpublishTutorial={handleUnpublishTutorial}
          />
        );
    }
  };
  
  return (
    <Box>
      {view === 'list' && (
        <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight="bold" color={THEME_COLORS.primary} gutterBottom>
            Tutorial Management
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Create, edit and manage interactive tutorials for your students.
          </Typography>
        </Paper>
      )}
      
      {getViewContent()}
      
      {/* Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TutorialManagement;